import {
  Component,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { Store, select } from '@ngrx/store';
import {
  Subject,
  map,
  withLatestFrom,
  debounceTime,
  switchMap,
  filter,
  combineLatest,
  of
} from 'rxjs';
import { formValueOnValid } from '@zerops/zef/forms';
import { zefDialogClose, selectZefDialogState } from '@zerops/zef/dialog';
import { selectZefProgressByType } from '@zerops/zef/progress';
import { FEATURE_NAME } from './control-plane-dialog.constant';
import {
  ControlPlaneForm,
  ControlPlaneFormValue
} from './control-plane-dialog.form';
import { controlPlaneDialogActions } from './control-plane-dialog.state';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import {
  ZagentAgentType,
  ZagentAuthType,
  ZagentDialogMode,
  ZagentRecipeContext,
  getMountableRecipeServices
} from '@zerops/zerops/core/zerops-services';
import { ServiceStackTypeCategories } from '@zerops/models/service-stack';

interface PerAgentAuthChange {
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
}

/**
 * Field-key maps for per-agent setValue() dispatches. Static so the
 * compiler verifies coverage when `ZagentAgentType` grows.
 */
const AUTH_TYPE_FIELD: Partial<Record<ZagentAgentType, keyof ControlPlaneFormValue>> = {
  'claude-code': 'authTypeClaudeCode',
  'codex': 'authTypeCodex',
  'antigravity': 'authTypeAntigravity',
  'grok': 'authTypeGrok',
  'cursor': 'authTypeCursor'
};

@Component({
  selector: 'z-control-plane-dialog',
  templateUrl: './control-plane-dialog.feature.html',
  styleUrl: './control-plane-dialog.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ControlPlaneDialogFeature extends ZefReactiveComponent {
  // # Deps
  #store = inject(Store);
  #form = inject(ControlPlaneForm);
  #projectEntity = inject(ProjectEntity);

  // # Event Streams
  onClose$ = new Subject<void>();
  onSubmit$ = new Subject<void>();
  onApplyConfig$ = new Subject<void>();
  onRegeneratePassword$ = new Subject<void>();
  onAuthTypeChange$ = new Subject<PerAgentAuthChange>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  actionKey = controlPlaneDialogActions.deploy.type;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  dialogMeta$ = this.dialogState$.pipe(map((data) => data?.meta));
  projectId$ = this.dialogState$.pipe(map((data) => data?.meta?.projectId));
  dialogMode$ = this.dialogState$.pipe(
    map((data) => (data?.meta?.dialogMode as ZagentDialogMode) ?? 'standalone')
  );
  recipeContext$ = this.dialogState$.pipe(
    map((data) => data?.meta?.recipeContext as ZagentRecipeContext | undefined)
  );
  formState$ = this.#form.state$;
  formValid$ = this.#form.valid$;
  formValue$ = this.#form.value$;
  deployLoading$ = this.#store.pipe(
    select(selectZefProgressByType(controlPlaneDialogActions.deploy.type)),
    map((progress) => !!progress)
  );

  project$ = combineLatest([
    this.projectId$,
    this.dialogMode$
  ]).pipe(
    switchMap(([ projectId, mode ]) => {
      if (!projectId || mode !== 'standalone') {
        return of(null);
      }
      return this.#projectEntity.entityByIdWithServiceStacks$(projectId);
    })
  );

  // Shared stream for mode/context/project combinations
  #modeContextProject$ = combineLatest([
    this.dialogMode$,
    this.recipeContext$,
    this.project$
  ]);

  // Services from project (standalone mode) or recipe context (recipe mode)
  // Only USER category services are shown (runtime services like Node.js, Python, etc.)
  availableServices$ = this.#modeContextProject$.pipe(
    map(([ mode, recipeContext, project ]) => {
      if (mode === 'recipe' && recipeContext?.services) {
        // Use recipe service definitions - filter to USER category only, exclude zcp*
        return getMountableRecipeServices(recipeContext.services)
          .map((s) => ({
            id: s.name,
            hostname: s.name,
            type: s.typeName,
            nameParts: this.#splitOnDev(s.name)
          }));
      }

      // Standard: use project services - filter to USER category only, exclude zcp*
      return project?.serviceStacks
        ?.filter((service) =>
          service.activeAppVersion &&
          service.serviceStackTypeInfo?.serviceStackTypeCategory === ServiceStackTypeCategories.User &&
          !service.name.toLowerCase().startsWith('zcp')
        )
        ?.map((service) => ({
          id: service.id,
          hostname: service.name,
          type: service.serviceStackTypeInfo?.serviceStackTypeName,
          nameParts: this.#splitOnDev(service.name)
        })) || [];
    })
  );

  #splitOnDev(name: string): { text: string; isDev: boolean }[] {
    return name.split(/(dev)/i)
      .filter((part) => part.length > 0)
      .map((part) => ({ text: part, isDev: part.toLowerCase() === 'dev' }));
  }

  // Mask quoted env values whose key name contains PASSWORD/TOKEN/KEY/SECRET
  // — preview only, the unmasked value still goes to the API on submit
  #maskSensitiveYaml(yaml: string): string {
    return yaml.replace(
      /^(\s+)([A-Z][A-Z0-9_]*(?:PASSWORD|TOKEN|KEY|SECRET)[A-Z0-9_]*):\s*"[^"]*"/gm,
      '$1$2: "••••••••••••••••"'
    );
  }

  existingServiceNames$ = this.#modeContextProject$.pipe(
    map(([ mode, recipeContext, project ]) => {
      if (mode === 'recipe' && recipeContext?.services) {
        return recipeContext.services.map((s) => s.name);
      }
      return project?.serviceStacks?.map((s) => s.name) || [];
    })
  );

  // YAML preview — derived live from form value + services + existing names,
  // debounced for Monaco re-render perf. NOT stored in form state, so typing
  // into form fields doesn't dispatch a setValue('yamlContent', ...) per
  // keystroke (previous design cost 1 extra SET_VALUE per char).
  maskedYaml$ = combineLatest([
    this.formValue$,
    this.availableServices$,
    this.existingServiceNames$
  ]).pipe(
    debounceTime(100),
    map(([ formValue, services, existingNames ]) => {
      const raw = this.#form.generateYamlContent(formValue, services, existingNames);
      return this.#maskSensitiveYaml(raw);
    })
  );

  // Whether to show deploy button (standalone) or apply button (recipe mode)
  isRecipeMode$ = this.dialogMode$.pipe(map((mode) => mode === 'recipe'));

  // # State resolver
  state = this.$connect({
    open: this.open$,
    projectId: this.projectId$,
    dialogMode: this.dialogMode$,
    formState: this.formState$,
    formValid: this.formValid$,
    formValue: this.formValue$,
    deployLoading: this.deployLoading$,
    availableServices: this.availableServices$,
    isRecipeMode: this.isRecipeMode$,
    maskedYaml: this.maskedYaml$
  });

  constructor() {
    super();

    // # Dispatcher
    this.$dispatchActions([
      // Close dialog (X button / backdrop click)
      this.onClose$.pipe(
        withLatestFrom(this.dialogMeta$),
        map(([ _, meta ]) => zefDialogClose({ key: FEATURE_NAME, meta }))
      ),

      // Per-agent auth type change — dispatches setValue on the
      // corresponding `authType*` field. The mirror sync stream below
      // then propagates to the legacy `authType`/`provider` fields.
      this.onAuthTypeChange$.pipe(
        map(({ agentType, authType }) => {
          const key = AUTH_TYPE_FIELD[agentType];
          if (!key) return null;
          return this.#form.setValue(key, authType);
        }),
        filter((action): action is NonNullable<typeof action> => action !== null)
      ),

      // Standalone mode: deploy control plane to existing project. YAML is
      // generated inline from the latest form value + services so the deploy
      // payload always reflects the current form state (no stale yamlContent
      // cache, no per-keystroke setValue for yaml regen).
      this.onSubmit$.pipe(
        formValueOnValid(this.#form),
        withLatestFrom(this.projectId$, this.dialogMode$, this.availableServices$, this.existingServiceNames$),
        filter(([ , , mode ]) => mode === 'standalone'),
        map(([ formValue, projectId, _mode, services, existingNames ]) => controlPlaneDialogActions.deploy({
          projectId,
          yamlContent: this.#form.generateYamlContent(formValue, services, existingNames),
          createIntegrationToken: formValue.enableAgent
        }))
      ),

      // Recipe mode: apply config and close dialog
      this.onApplyConfig$.pipe(
        formValueOnValid(this.#form),
        withLatestFrom(this.dialogMeta$),
        map(([ _, meta ]) => zefDialogClose({ key: FEATURE_NAME, meta: { ...meta, applied: true } }))
      ),

      this.onRegeneratePassword$.pipe(
        map(() => {
          const newPassword = this.#form.generateNewPassword();
          return this.#form.setValue('vscodePassword', newPassword);
        })
      )
    ]);
  }

}
